import React, { Component, useState, useEffect, useMemo } from 'react';
import Session from '../auth/Session';
import utilities from '../util/utilities';
import { FaSort } from 'react-icons/fa'
import '../layout/site.css';
import AuthFunctions from '../services/AuthService';
import {
    Container, Card, CardBody, CardTitle, Button,
    Table, Modal, ModalHeader, ModalBody, Spinner, Row, Col, Input, FormGroup, Label
} from "reactstrap";
import FuelRateForm from "./FuelRateForm";
import * as dayjs from 'dayjs';
import useSortableData from "./useSortableData";
import SortableHeader from "./SortableHeader";

const FuelRateGrid = () => {
    const apiBaseUrl = '';
    const [fuelRates, setFuelRates] = useState([]);
    const [loading, setLoading] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM-01'));
    const [modalOpen, setModalOpen] = useState(false);
    const [editingFuelRate, setEditingFuelRate] = useState(null);
    const [monthsRange, setMonthsRange] = useState([{
        value: "", label: ""
    }]);

    const sortGetters = useMemo(() => ({
        rate: (f) => Number(f.rate),
        monthYear: (f) => (f.monthYear ? new Date(f.monthYear) : null),
    }), []);

    const { items: sortedFuelRates, sortConfig, requestSort } = useSortableData(
        fuelRates,
        { key: "state", direction: "asc", getters: sortGetters }
    );

    // Load routes ONLY when a subcustomer is selected
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                let startMonth = dayjs('2025-01-01', 'YYYY-MM-DD');
                const currentMonth = dayjs(dayjs().format('YYYY-MM-01'), 'YYYY-MM-DD');
                const monthList = [];

                while (startMonth <= currentMonth) {
                    monthList.push({ value: startMonth.format('YYYY-MM-DD'), label: startMonth.format('MMM-YYYY') });
                    startMonth = startMonth.add(1, 'month');
                }

                setMonthsRange(monthList);

                const token = Session.getToken();
                const response = await fetch(`/api/FuelRate/${selectedMonth}`, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setFuelRates(data)
                }
            } catch (e) {
                console.error("Failed to load transaction types", e);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, [selectedMonth]);


    const handleEdit = (fuelRate) => {
        setEditingFuelRate(fuelRate);
        setModalOpen(true);
    };

    const handleSaved = () => {
        setModalOpen(false);
        const loadData = async () => {
            setLoading(true);
            try {
                const token = Session.getToken();

                const response = await fetch(`/api/FuelRate/${selectedMonth}`, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setFuelRates(data)
                }
            } catch (e) {
                console.error("Failed to load transaction types", e);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    };

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>Fuel Rate Management</h5></Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={4}>
                            <Input
                                type="select"
                                value={selectedMonth}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    setSelectedMonth(value);
                                }}
                            >
                                <option value="">-- Select Month/Year --</option>
                                {monthsRange.map(m => (
                                    <option key={m.value} value={m.value}>
                                        {m.label}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                    </Row>

                    <Table bordered striped>
                        <thead>
                            <tr>
                                <SortableHeader label="State" sortKey="state" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Fuel Rate" sortKey="rate" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Month/Year" sortKey="monthYear" sortConfig={sortConfig} onSort={requestSort} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedFuelRates.map(fuelRate => (
                                <tr key={fuelRate.id}>
                                    <td>{fuelRate.state}</td>
                                    <td>${Number(fuelRate.rate).toFixed(3)}</td>
                                    <td>{dayjs(fuelRate.monthYear, "YYYY-MM-DD").format('MMM-YYYY')}</td>
                                    <td>
                                        <Button size="sm" color="info" onClick={() => handleEdit(fuelRate)}>Edit</Button>{" "}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
                        <ModalHeader toggle={() => setModalOpen(false)}>
                            Edit Fuel Rate
                        </ModalHeader>
                        <ModalBody>
                            <FuelRateForm
                                fuelRateItem={editingFuelRate}
                                apiBaseUrl={apiBaseUrl}
                                onSuccess={handleSaved}
                                onCancel={() => setModalOpen(false)}
                            />
                        </ModalBody>
                    </Modal>
                </CardBody>
            </Card>
        </Container>
    )
}

export default FuelRateGrid;